import { type ModalProps } from '~/util';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { useAppForm } from '~/util/hooks/useAppForm';
import { formOptions } from '@tanstack/react-form';
import { useList } from '~/util/hooks/useList';
import { useApiMutation } from '~/util/hooks/useApiMutation';

import { useEffect } from 'react';
import { packFormInput, packFormSchema } from '~/util/forms/packtier';
import { createPackMutation } from '~/util/mutations/staff/packs';
import { PackTier } from '~/util/types/packs';
import { PacksTierTags } from '~/partials/Tags';

export const NewPackSheet: React.FC<
    {
        packtier: PackTier;
    } & ModalProps
> = ({ packtier, ...modalProps }) => {
    const list = useList();

    const mutation = useApiMutation({
        ...createPackMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const form = useAppForm(
        formOptions({
            formId: 'add-pack-staff',
            defaultValues: {
                list,
                tier: packtier.id,
            } as packFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: packFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate(value);
            },
        }),
    );

    useEffect(() => {
        form.reset({
            list,
            tier: packtier.id,
        });
    }, [list, packtier, form]);

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Create Pack</SheetTitle>
                        <SheetDescription>
                            Create a new pack. It will be added to the{' '}
                            {packtier.name} tier afterwards.
                        </SheetDescription>
                    </SheetHeader>

                    <form
                        id="add-pack-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <PacksTierTags item={packtier} />

                            <form.AppField name="name">
                                {(field) => (
                                    <field.TextField
                                        label="Name"
                                        required
                                        placeholder="Name of the new pack"
                                    />
                                )}
                            </form.AppField>

                            <form.AppField name="levels">
                                {(field) => (
                                    <field.LevelsField
                                        list={list}
                                        label="Levels"
                                        required
                                    />
                                )}
                            </form.AppField>
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <form.AppForm>
                                <form.SubscribeButton
                                    label="Create Pack"
                                    classname="w-11/12"
                                />
                            </form.AppForm>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
};
